<template>
  <div>
    <BaseReportTable
      :resultados="resultados"
      :total-items="totalItems"
      :items-por-pagina="itemsPorPagina"
      :pagina-actual="paginaActual"
      :loading="loading"
      :headers="infoCobranzasHeaders"
      :es-operacion="esOperacion"
      :item-class="getRowClass"
      @cambiar-pagina="$emit('cambiar-pagina', $event)"
      @cambiar-items-por-pagina="$emit('cambiar-items-por-pagina', $event)"
    >
      <template v-slot:item.legajo="{ item }">
        <template v-if="!item._esSubtotal">
          <a
            v-if="item.legajo"
            :href="getGestionarUrl(item.legajo)"
            target="_blank"
            class="legajo-link"
          >
            {{ item.legajo }}
          </a>
          <template v-else>-</template>
        </template>
      </template>

      <template v-slot:item.operatoria_linea="{ item }">
        <template v-if="item._esSubtotal">
          <strong class="subtotal-label">{{ item.operatoria_linea }}</strong>
        </template>
        <template v-else>
          {{ item.operatoria_linea || '-' }}
        </template>
      </template>

      <template v-slot:item.nombre_razon_social="{ item }">
        <template v-if="!item._esSubtotal">
          {{ item.nombre_razon_social ? item.nombre_razon_social.toUpperCase() : '-' }}
        </template>
      </template>

      <template v-slot:item.cuit="{ item }">
        <template v-if="!item._esSubtotal">
          {{ item.cuit || '-' }}
        </template>
      </template>

      <template v-slot:item.departamento="{ item }">
        <template v-if="!item._esSubtotal">
          {{ getDepartamentoNombre(item) }}
        </template>
      </template>

      <template v-slot:item.persona="{ item }">
        <template v-if="!item._esSubtotal">
          <span class="persona-text">{{ item.persona || '-' }}</span>
        </template>
      </template>

      <template v-slot:item.fecha_ingreso="{ item }">
        <template v-if="!item._esSubtotal">
          {{ formatearFecha(item.fecha_ingreso) }}
        </template>
      </template>

      <template v-slot:item.estado="{ item }">
        <template v-if="!item._esSubtotal">
          <span class="estado-text">{{ item.estado || '-' }}</span>
        </template>
      </template>

      <!-- Templates para campos específicos de Info Cobranzas Proyectadas -->
      <template v-slot:item.periodo="{ item }">
        <template v-if="!item._esSubtotal">
          <div class="text-center">${{ formatoNumero(item.periodo || 0) }}</div>
        </template>
      </template>

      <!-- Templates dinámicos para columnas de meses -->
      <template
        v-for="header in columnasMeses"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        <div class="text-center" :key="header.value">
          <template v-if="item._esSubtotal">
            <strong class="subtotal-value">${{ formatoNumero(getSubtotalParaColumna(item, header.value)) }}</strong>
          </template>
          <template v-else>
            ${{ formatoNumero(item[header.value] || 0) }}
          </template>
        </div>
      </template>

      <!-- Slot para fila de totales -->
      <template v-slot:totals-row="{ totals }">
        <!-- Total de Período (BaseReportTable ya agregó las 8 celdas anteriores) -->
        <td class="text-center">
          <strong class="totals-text">${{ formatoNumero(getTotalPeriodo()) }}</strong>
        </td>

        <!-- Totales dinámicos para cada columna de mes -->
        <td
          v-for="header in columnasMeses"
          :key="`total_${header.value}`"
          class="text-center"
        >
          <strong class="totals-text">${{ formatoNumero(getTotalPorColumna(header.value)) }}</strong>
        </td>
      </template>
    </BaseReportTable>
  </div>
</template>

<script>
import BaseReportTable from './BaseReportTable.vue';
import ReportCalculations from '../ReportCalculations';

export default {
  name: 'InfoCobranzasProyectadasReportTable',
  components: {
    BaseReportTable,
  },
  mixins: [ReportCalculations],
  props: {
    resultados: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPorPagina: {
      type: Number,
      default: 10,
    },
    paginaActual: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    esOperacion: {
      type: String,
      default: '',
    },
    totales: {
      type: Object,
      default: () => ({}),
    },
    fechaDesde: {
      type: String,
      default: null,
    },
    fechaHasta: {
      type: String,
      default: null,
    },
  },
  computed: {
    infoCobranzasHeaders() {
      // Headers base comunes
      const headersBase = [
        { text: 'Legajo', value: 'legajo' },
        { text: 'Operatoria / Línea', value: 'operatoria_linea', width: '200px' },
        { text: 'Apellido y Nombre / Razón Social', value: 'nombre_razon_social' },
        { text: 'CUIT', value: 'cuit' },
        { text: 'Departamento', value: 'departamento' },
        { text: 'Persona', value: 'persona' },
        { text: 'Fecha de Ingreso', value: 'fecha_ingreso' },
        { text: 'Estado', value: 'estado' },
        { text: 'Período', value: 'periodo' },
      ];

      // Calcular qué columnas de meses mostrar dinámicamente
      const fechasHeaders = this.calcularColumnasMesesConDatos();

      return [...headersBase, ...fechasHeaders];
    },
    columnasMeses() {
      // Obtener solo los headers de meses (excluyendo los headers base)
      const fechasHeaders = this.calcularColumnasMesesConDatos();
      return fechasHeaders;
    },
    mesesVisibles() {
      // Determinar qué meses están en el rango de fechas específico
      const mesesEnRango = new Set();

      // Si hay rango de fechas, determinar SOLO los meses en ese rango
      if (this.fechaDesde && this.fechaHasta) {
        const fechaInicio = new Date(this.fechaDesde);
        const fechaFin = new Date(this.fechaHasta);

        // Agregar todos los meses en el rango específico
        const fecha = new Date(fechaInicio);
        while (fecha <= fechaFin) {
          const mes = fecha.getMonth() + 1; // getMonth() devuelve 0-11, necesitamos 1-12
          mesesEnRango.add(mes);
          fecha.setMonth(fecha.getMonth() + 1);
        }

        // Retornar solo los meses del rango de fechas
        return Array.from(mesesEnRango).sort((a, b) => a - b);
      }

      // Si no hay fechas específicas, revisar qué meses tienen datos reales
      const mesesConDatos = new Set();
      if (this.resultados && this.resultados.length > 0) {
        this.resultados.forEach(item => {
          // Para registros normales, revisar las columnas fecha_X
          if (!item._esSubtotal) {
            for (let i = 1; i <= 12; i++) {
              if (item[`fecha_${i}`] && item[`fecha_${i}`] > 0) {
                mesesConDatos.add(i);
              }
            }
          } else {
            // Para subtotales, revisar el array subtotales
            if (item.subtotales && Array.isArray(item.subtotales)) {
              item.subtotales.forEach(sub => {
                const [mesStr] = sub.mesAño.split('-');
                const mesNum = parseInt(mesStr, 10);
                if (mesNum >= 1 && mesNum <= 12 && sub.totalMes > 0) {
                  mesesConDatos.add(mesNum);
                }
              });
            }
          }
        });
      }

      return Array.from(mesesConDatos).sort((a, b) => a - b);
    },
  },
  methods: {
    getGestionarUrl(legajo) {
      const baseUrl = process.env.VUE_APP_GESTIONAR_URL || 'http://localhost:8080';
      return `${baseUrl}/gestionar/${legajo}`;
    },
    getDepartamentoNombre(item) {
     
      // Si el item ya tiene el campo departamento mapeado, usarlo
      if (item.departamento) {
        return item.departamento;
      }

      // Intentar obtener departamento desde diferentes estructuras
      if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
        return item.localidad.departamento.nombre;
      }
      if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
        return item.cliente.localidad.departamento.nombre;
      }
      // Fallback a localidad si no hay departamento
      if (item.cliente && item.cliente.localidad) {
        return item.cliente.localidad;
      }
      if (item.localidad && typeof item.localidad === 'string') {
        return item.localidad;
      }
      if (item.localidad && item.localidad.nombre) {
        return item.localidad.nombre;
      }
      return '-';
    },
    getRowClass(item) {
      if (item._esSubtotal) {
        return 'subtotal-row-cobranzas';
      }
      return '';
    },
    getSubtotalParaMes(item, mes) {
      if (!item._esSubtotal || !item.subtotales) {
        return 0;
      }

      // Buscar en el array de subtotales el mes correspondiente
      const subtotal = item.subtotales.find(sub => {
        // El mesAño viene como "MM-YYYY", extraemos el mes
        const [mesStr] = sub.mesAño.split('-');
        const mesNum = parseInt(mesStr, 10);
        return mesNum === mes;
      });

      return subtotal ? subtotal.totalMes : 0;
    },
    getSubtotalParaColumna(item, columnValue) {
      if (!item._esSubtotal || !item.subtotales) {
        return 0;
      }

      // columnValue tiene formato "fecha_2_2026", extraer mes y año
      const partes = columnValue.split('_');
      if (partes.length !== 3) return 0;

      const mes = parseInt(partes[1], 10);
      const año = parseInt(partes[2], 10);

      // Buscar en el array de subtotales el que coincida con mes-año
      const subtotal = item.subtotales.find(sub => {
        // El mesAño viene como "MM-YYYY"
        const [mesStr, añoStr] = sub.mesAño.split('-');
        const mesNum = parseInt(mesStr, 10);
        const añoNum = parseInt(añoStr, 10);
        return mesNum === mes && añoNum === año;
      });

      return subtotal ? subtotal.totalMes : 0;
    },
    getTotalPeriodo() {
      let total = 0;
      this.resultados.forEach(item => {
        if (!item._esSubtotal && item.periodo) {
          total += parseFloat(item.periodo) || 0;
        }
      });
      return total;
    },
    getTotalPorMes(mes) {
      let total = 0;
      this.resultados.forEach(item => {
        // Solo sumar registros normales, NO los subtotales
        if (!item._esSubtotal) {
          total += parseFloat(item[`fecha_${mes}`]) || 0;
        }
      });
      return total;
    },
    getTotalPorColumna(columnKey) {
      let total = 0;
      this.resultados.forEach(item => {
        // Solo sumar registros normales, NO los subtotales ni headers de fideicomiso
        if (!item._esSubtotal && !item._esFideicomisoHeader) {
          total += parseFloat(item[columnKey]) || 0;
        }
      });
      return total;
    },
    calcularColumnasMesesConDatos() {
      const meses = {
        1: 'ene', 2: 'feb', 3: 'mar', 4: 'abr', 5: 'may', 6: 'jun',
        7: 'jul', 8: 'ago', 9: 'sep', 10: 'oct', 11: 'nov', 12: 'dic'
      };

      const fechasHeaders = [];
      const periodosConAño = new Map();

      // Detectar períodos desde las propiedades procesadas fecha_MES_AÑO
      if (this.resultados && this.resultados.length > 0) {
        this.resultados.forEach(item => {
          if (!item._esSubtotal) {
            // Buscar todas las propiedades que empiecen con "fecha_" y terminen con "_YYYY"
            Object.keys(item).forEach(key => {
              if (key.startsWith('fecha_') && item[key] && item[key] > 0) {
                // Extraer mes y año del formato fecha_MES_AÑO
                const partes = key.split('_');
                if (partes.length === 3) {
                  const mes = parseInt(partes[1], 10);
                  const año = parseInt(partes[2], 10);

                  if (mes >= 1 && mes <= 12 && año > 2000) {
                    // Crear clave única por mes-año para ordenamiento
                    const clave = `${año}-${mes.toString().padStart(2, '0')}`;
                    periodosConAño.set(clave, { mes, año });
                  }
                }
              }
            });
          }
        });
      }

      // Generar headers ordenados cronológicamente
      Array.from(periodosConAño.keys())
        .sort()
        .forEach(clave => {
          const { mes, año } = periodosConAño.get(clave);
          const añoCorto = año.toString().slice(-2);
          fechasHeaders.push({
            text: `${meses[mes]} ${añoCorto}`,
            value: `fecha_${mes}_${año}`, // Incluir año en el value para hacer único
            width: '120px'
          });
        });

      console.log('📅 Info Cobranzas Proyectadas - Columnas de meses:', fechasHeaders.map(h => `${h.text} (${h.value})`));
      console.log('📅 Info Cobranzas Proyectadas - Períodos detectados:', Array.from(periodosConAño.keys()));

      return fechasHeaders;
    },
    formatearFecha(fecha) {
      if (!fecha) return '-';
      try {
        return new Date(fecha).toLocaleDateString('es-ES');
      } catch {
        return fecha;
      }
    },
  },
  emits: ['cambiar-pagina', 'cambiar-items-por-pagina'],
};
</script>

<style scoped>
.persona-text {
  color: #000000;
  font-weight: normal;
}

.estado-text {
  color: #000000;
  font-weight: normal;
}

/* Estilos para filas de subtotales de Info Cobranzas Proyectadas */
.v-data-table >>> .subtotal-row-cobranzas {
  background: linear-gradient(135deg, #e8f4fd 0%, #d1ecf1 100%) !important;
  border-top: 2px solid #17a2b8 !important;
  border-bottom: 2px solid #17a2b8 !important;
  border-left: 4px solid #17a2b8 !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 4px rgba(23,162,184,0.15) !important;
}

.v-data-table >>> .subtotal-row-cobranzas:hover {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(23,162,184,0.2) !important;
  transition: all 0.2s ease-in-out;
}

.subtotal-label {
  font-weight: 700;
  color: #17a2b8;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.subtotal-value {
  font-size: 12px !important;
  font-family: 'Roboto', sans-serif !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  font-weight: 700;
  color: #17a2b8;
}

.legajo-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.legajo-link:hover {
  color: #1565c0;
  text-decoration: underline;
}
</style>